import React from 'react';
import { Box } from '@mui/material';
import { useColors, FadeIn } from './shared';
import DataBoard from './DataBoard';

const MISTAKE = '#E4446E';
// Transparent space above the board in its canvas, so hover labels on the far
// squares aren't clipped. The board area itself is unchanged (pulled up by the
// same amount).
const BOARD_HEADROOM = 160;
// Transparent space below the board's area, so its near corner isn't cut and
// can run over the start of the archetype section (layout unchanged).
const BOARD_FOOTROOM = 420;
// The diagnosis preview. The first six are the hero dashboard's weaknesses with
// the same Elo costs (they add up to its −142); the rest are smaller patterns
// the engine can also surface, in a scrollable list.
const LEAKS = [
  { name: 'Rook endgames', elo: 42 },
  { name: 'Time scrambles', elo: 31 },
  { name: 'Isolated d-pawn', elo: 28 },
  { name: 'Knight placement', elo: 19 },
  { name: 'Sicilian Defense prep', elo: 14 },
  { name: 'Back-rank threats', elo: 8 },
  { name: 'Converting winning positions', elo: 7 },
  { name: 'Pawn-break timing', elo: 6 },
  { name: 'Opposite-side castling attacks', elo: 6 },
  { name: 'Premature queen trades', elo: 5 },
  { name: 'Bishop-pair handling', elo: 5 },
  { name: 'Missed knight forks', elo: 4 },
  { name: 'King activity in endgames', elo: 4 },
  { name: 'Overextended pawns', elo: 3 },
];
const TOP = 6;
const TOP_ELO = LEAKS.slice(0, TOP).reduce((sum, l) => sum + l.elo, 0); // 142, as in the hero
const PURPLE = '#8A74E4';

// Card listing the mistakes the engine could find, costliest first. The list
// scrolls; bars fill in when the card scrolls into view.
function DiagnosisPreview({ c }) {
  const ref = React.useRef(null);
  const listRef = React.useRef(null);
  const [shown, setShown] = React.useState(false);

  // The list scrolls on its own, slowly and forever: it's rendered twice back to
  // back, and once the first copy has scrolled out of view the position wraps by
  // one copy's height, so the loop is seamless. Runs only while the card is on
  // screen; still for reduced motion.
  const loopRef = React.useRef(null); // the second copy, to measure one cycle
  React.useEffect(() => {
    const el = listRef.current;
    if (!el) return undefined;
    let reduce = false;
    try { reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (err) { /* keep */ }
    if (reduce) return undefined;
    let raf = null;
    let onScreen = false;
    let last = 0;
    let pos = 0;
    const step = (now) => {
      raf = requestAnimationFrame(step);
      if (!onScreen) { last = now; return; }
      const dt = Math.min(0.05, (now - (last || now)) / 1000);
      last = now;
      const cycle = loopRef.current ? loopRef.current.offsetTop - el.firstChild.offsetTop : 0;
      if (!cycle) return;
      pos = (pos + 50 * dt) % cycle; // px per second
      el.scrollTop = pos;
    };
    const obs = new IntersectionObserver(([e]) => { onScreen = e.isIntersecting; }, { threshold: 0.1 });
    obs.observe(el);
    raf = requestAnimationFrame(step);
    return () => { cancelAnimationFrame(raf); obs.disconnect(); };
  }, []);
  React.useEffect(() => {
    if (!ref.current) return undefined;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) { setShown(true); obs.disconnect(); }
    }, { threshold: 0.4 });
    obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  return (
    <Box
      ref={ref}
      sx={{
        borderRadius: '20px',
        bgcolor: '#1e1e23',
        border: '1px solid rgba(236,226,219,0.07)',
        p: { xs: 2.5, md: 3.5 },
      }}
    >
      {/* Same type as the hero mockup's "Weakness Ranking" heading — its
          computed style (the page's global heading rules end up giving it
          weight 650 and tight tracking). A div, so those rules don't apply on
          top of these. */}
      <Box
        sx={{
          mb: { xs: 3, md: 4.5 },
          fontSize: '1.15rem',
          // Cream (the mockup's heading uses a dark grey; here it needs to read).
          color: c.textPrimary,
          fontWeight: 650,
          letterSpacing: '-0.03em',
          textTransform: 'uppercase',
          lineHeight: 1.2,
        }}
      >
        What our engine could find in your games
      </Box>

      {/* The self-scrolling list, softly faded at top and bottom. */}
      <Box
        ref={listRef}
        sx={{
          maxHeight: { xs: 230, md: 'clamp(220px, 30vh, 290px)' },
          overflow: 'hidden',
          pb: 3,
          pt: 1.5,
          maskImage: 'linear-gradient(to bottom, transparent 0%, #000 7%, #000 82%, transparent 100%)',
          WebkitMaskImage: 'linear-gradient(to bottom, transparent 0%, #000 7%, #000 82%, transparent 100%)',
        }}
      >
        {[0, 1].map((copy) => (
        <Box
          key={copy}
          ref={copy === 1 ? loopRef : undefined}
          aria-hidden={copy === 1 ? 'true' : undefined}
          sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 2, md: 2.2 }, pb: { xs: 2, md: 2.2 } }}
        >
          {LEAKS.map((leak, i) => {
            const color = i === 0 ? MISTAKE : PURPLE;
            return (
              <Box key={leak.name}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 2, mb: 0.9 }}>
                  <Box sx={{ color: c.textPrimary, fontSize: 'clamp(1.05rem, 1.3vw, 1.3rem)', fontWeight: 500 }}>{leak.name}</Box>
                  <Box sx={{ color, fontSize: 'clamp(1.05rem, 1.3vw, 1.3rem)', fontWeight: 700, whiteSpace: 'nowrap' }}>−{leak.elo} Elo</Box>
                </Box>
                <Box sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(236,226,219,0.07)', overflow: 'hidden' }}>
                  <Box
                    sx={{
                      height: '100%',
                      borderRadius: 3,
                      bgcolor: color,
                      opacity: i < TOP ? 1 : 0.6,
                      width: shown ? `${(leak.elo / LEAKS[0].elo) * 100}%` : '0%',
                      transition: `width 1.1s cubic-bezier(0.22, 1, 0.36, 1) ${0.15 + Math.min(i, 6) * 0.1}s`,
                    }}
                  />
                </Box>
              </Box>
            );
          })}
        </Box>
        ))}
      </Box>

      <Box
        sx={{
          mt: { xs: 3, md: 4 },
          color: c.textSecondary,
          fontSize: 'clamp(1.1rem, 1.3vw, 1.3rem)',
          lineHeight: 1.4,
        }}
      >
        Your top {TOP} recurring mistakes cost −{TOP_ELO} Elo per 100 games.
      </Box>
    </Box>
  );
}

function PainSection() {
  const c = useColors();

  return (
    <Box sx={{ pt: 6, pb: { xs: 16, md: 52 }, px: { xs: 2.5, md: '10vw' } }}>
      {/* Header */}
      <FadeIn>
        <Box sx={{ textAlign: 'center', mb: 2, maxWidth: 1000, mx: 'auto' }}>
          <h2
            style={{
              color: c.textPrimary,
              fontSize: 'clamp(2.2rem, 5.5vw, 4.2rem)',
              fontWeight: 600,
              margin: 0,
              letterSpacing: '-0.02em',
              lineHeight: 1.15,
            }}
          >
            <span style={{ whiteSpace: 'nowrap' }}>You keep making the same mistakes</span>
            <br />
            <span style={{ whiteSpace: 'nowrap' }}>
              and <span style={{ color: c.accent }}>you don't even realize it.</span>
            </span>
          </h2>
          <Box
            component="p"
            sx={{
              m: 0,
              mt: { xs: 2, md: 2.5 },
              color: c.textSecondary,
              fontSize: 'clamp(1.05rem, min(1.6vw, 2.4vh), 1.5rem)',
              lineHeight: 1.4,
              letterSpacing: '-0.01em',
              whiteSpace: { md: 'nowrap' },
            }}
          >
            You don{'’'}t need more theory. You don{'’'}t need to play more games.
          </Box>
        </Box>
      </FadeIn>

      <FadeIn delay={0.15}>
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: '1.25fr 1fr' },
            alignItems: 'center',
            // Pulled up by the subtitle's height so the board stays where it was.
            mt: { md: '-50px' },
            gap: { xs: 2, md: 4 },
            maxWidth: 1340,
            mx: 'auto',
          }}
        >
          {/* Left: the data board, seen low at an angle. It bleeds past the
              section's side padding and runs straight off the page edge, so only
              its right part reads. */}
          <Box
            sx={{
              position: 'relative',
              // Nudged down; the fades (mask) live on this same box, so they
              // move with the board. A relative offset, so layout doesn't change.
              top: { md: '60px' },
              // Decorative and it overlaps the next section: never take clicks
              // (the hover lens listens on the window).
              pointerEvents: 'none',
              // Extra room above the board for the hover labels (see BOARD_HEADROOM).
              height: { xs: `calc(420px + ${BOARD_HEADROOM + BOARD_FOOTROOM}px)`, md: `calc(clamp(640px, 92vh, 960px) + ${BOARD_HEADROOM + BOARD_FOOTROOM}px)` },
              mt: `-${BOARD_HEADROOM}px`,
              // Runs past the bottom of the section into the next one (the pain
              // Section is stacked above it), fading out on the way.
              mb: { xs: `calc(-96px - ${BOARD_FOOTROOM}px)`, md: `calc(-1 * clamp(260px, 34vh, 380px) - ${BOARD_FOOTROOM}px)` },
              // Reach the page edge at any width: the section's 10vw padding, plus
              // whatever the 1340px-max grid is centred in on wide screens.
              ml: { xs: -2.5, md: 'calc(-10vw - max(0px, (80vw - 1340px) / 2))' },
              maskImage: 'linear-gradient(to right, transparent 0%, #000 14%, #000 77%, transparent 96%), linear-gradient(to bottom, #000 0%, #000 100%)',
              WebkitMaskImage: 'linear-gradient(to right, transparent 0%, #000 14%, #000 77%, transparent 96%), linear-gradient(to bottom, #000 0%, #000 100%)',
              maskComposite: 'intersect',
              WebkitMaskComposite: 'source-in',
            }}
          >
            <DataBoard headroom={BOARD_HEADROOM} footroom={BOARD_FOOTROOM} sx={{ position: 'absolute', inset: 0, width: '100%', height: '100%', display: 'block' }} />
          </Box>

          {/* Right: the setup, the payoff, and a preview of what a diagnosis
              actually shows — one left-aligned column, as tall as the board. */}
          <Box
            sx={{
              color: c.textPrimary, letterSpacing: '-0.02em', maxWidth: 540, mx: { xs: 'auto', md: 0 },
              // Nudged down to sit on the board's visual centre. A relative offset,
              // so the grid row (and the board) don't move.
              position: 'relative', top: { md: 'clamp(215px, 26vh, 300px)' },
              // Symmetric negative margins: the column stays centred but no longer
              // makes the grid row taller than the board, so the board (and the
              // section's height) stay exactly where they were.
              my: { md: '-170px' },
            }}
          >
            <Box
              sx={{
                fontSize: 'clamp(2rem, min(3.4vw, 4.6vh), 3.1rem)',
                // Centred over the card below.
                textAlign: 'center',
                fontWeight: 700,
                lineHeight: 1.02,
                mb: { xs: 3.5, md: 4.5 },
              }}
            >
              You need a <span style={{ color: MISTAKE }}>diagnosis.</span>
            </Box>

            <DiagnosisPreview c={c} />

            {/* How the engine gets there, and what you get out of it. */}
            <Box sx={{ mt: { xs: 2.5, md: 3 }, color: c.textPrimary, fontSize: 'clamp(1.1rem, 1.3vw, 1.3rem)', lineHeight: 1.6,
              // Same inset as the card's content (its padding + 1px border), so the
              // text lines up with the card's text above it.
              px: { xs: 'calc(20px + 1px)', md: 'calc(28px + 1px)' },
              textAlign: 'left' }}>
              Our algorithms check every move you play against the best move and against
              what players at your rating usually find. A mistake only counts once it
              keeps coming back, so you see patterns, not one-off blunders. Each pattern
              is ranked by how often it happens and how many points it costs you.
            </Box>
          </Box>
        </Box>
      </FadeIn>
    </Box>
  );
}

export default PainSection;
